import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import logo from "../../../Assets/Images/logo.png";
import axios from "axios";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import { Eye, EyeOff } from "lucide-react";
import { fetchGeneralCountries } from "../../../Apis/OrganizationLocation";
import SelectWithImage from "../../../Components/Inputs/SelectWithImage";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const SpinSVG = () => (
  <svg width="18" height="18" viewBox="0 0 15 15" fill="none"
    style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
    <circle cx="7.5" cy="7.5" r="5.5" stroke="currentColor" strokeOpacity=".3" strokeWidth="2.5" fill="none" />
    <path d="M7.5 2A5.5 5.5 0 0 1 13 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

const FreeTrialPage = ({
  appName = "Your App Name",
  description,
}) => {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState(null);
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);

  const [showConflictModal, setShowConflictModal] = useState({
    show: false,
    title: "",
    message: "",
    actionType: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchGeneralCountries()
      .then((data) => {
        const formatted = data.countries.map((item) => ({
          name: item.country_name,
          code: item.country_code,
          phone_code: item.country_phone_code,
        }));
        setCountries(formatted);
        setCountryCode(formatted.find((item) => item.code === "IN"));
      })
      .catch((err) => toast.error(err.message));
  }, []);

  const validateForm = () => {
    const formErrors = {};
    let isValid = true;

    if (!fullName.trim()) {
      formErrors.fullName = "Full Name is required";
      isValid = false;
    }

    if (!email) {
      formErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!password) {
      formErrors.password = "Password is required";
      isValid = false;
    } else if (password.length < 8) {
      formErrors.password = "Password must be at least 8 characters";
      isValid = false;
    }

    if (!phone) {
      formErrors.phone = "Phone number is required";
      isValid = false;
    } else {
      const parsed = parsePhoneNumberFromString(phone, countryCode?.code);
      if (!parsed || !parsed.isValid()) {
        formErrors.phone = "Enter a valid phone number";
        isValid = false;
      } else {
        setPhone(parsed.nationalNumber);
      }
    }

    if (!agree) {
      formErrors.agree = "You must agree to the Terms of Service";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const payload = {
        email,
        full_name: fullName,
        hash_password: password,
        phone,
        country: `${countryCode.name}`,
        phone_code: `${countryCode.phone_code}`,
      };

      let res = await axios.post(
        `${MAIN_URL}/api/auth/create-user`,
        { email, phone },
        { headers: getApiHeaders() }
      );

      navigate("/start-free-trial/verify-otp", { state: { ...payload, otp: res.data.otp } });
    } catch (error) {
      if (error.response && error.response.data) {
        const { errors, message } = error.response.data;
        const status = error.response.status;

        if (status === 409 && message === "Phone number already exists.") {
          setShowConflictModal({
            show: true,
            title: "Phone Number Already Registered",
            message:
              "This phone number is already registered. Would you like to log in instead or continue registration?",
            actionType: "phone",
          });
          setErrors((prev) => ({ ...prev, phone: "Phone number already in use" }));
          toast.error("Phone number already registered.");
          return;
        }

        if (status === 409 && message === "User already Exist.") {
          setShowConflictModal({
            show: true,
            title: "Email Already Registered",
            message:
              "This email is already registered. Would you like to log in instead or continue registration?",
            actionType: "email",
          });
          setErrors((prev) => ({ ...prev, email: "Email already in use" }));
        } else if (errors) {
          setErrors(errors);
        } else {
          toast.error(message || "Registration failed.");
        }
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ems-auth-root">

      {/* ── Left panel — registration card ── */}
      <div className="ems-auth-left">
        <div className="ems-auth-card">

          <div className="ems-auth-brand">
            <img src={logo} alt="Logo" className="ems-auth-brand-logo" />
          </div>

          <h1 className="ems-auth-h">Start Free Trial</h1>
          <p className="ems-auth-sub">Create your account and get started today.</p>

          <form
            onSubmit={handleSubmit}
            noValidate
            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
          >
            <div className="ems-auth-field">
              <label htmlFor="trial-name">Full Name</label>
              <input
                id="trial-name"
                type="text"
                className="ems-fm-input"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => { setFullName(e.target.value); setErrors((p) => ({ ...p, fullName: "" })); }}
              />
              {errors.fullName && <span className="ems-fm-err">{errors.fullName}</span>}
            </div>

            <div className="ems-auth-field">
              <label htmlFor="trial-email">Email Address</label>
              <input
                id="trial-email"
                type="email"
                className="ems-fm-input"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
              />
              {errors.email && <span className="ems-fm-err">{errors.email}</span>}
            </div>

            <div className="ems-auth-field">
              <label htmlFor="trial-pw">Password</label>
              <div className="ems-auth-pw">
                <input
                  id="trial-pw"
                  type={showPassword ? "text" : "password"}
                  className="ems-fm-input"
                  placeholder="Create a secure password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors((p) => ({ ...p, password: "" })); }}
                />
                <button
                  type="button"
                  className="ems-auth-pw-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password
                ? <span className="ems-fm-err">{errors.password}</span>
                : <span style={{ fontSize: 12, color: "var(--ems-faint)", fontWeight: 500 }}>Minimum 8 characters, with at least one number or symbol.</span>
              }
            </div>

            <div className="ems-auth-field">
              <label>Phone Number</label>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <SelectWithImage
                  value={countryCode}
                  error={false}
                  options={countries}
                  optionkey={"phone_code"}
                  onChange={(_e, val) => setCountryCode(val)}
                  width={"60%"}
                  minWidth="20px"
                  disabled={false}
                  searchKeys={["name", "phone_code"]}
                />
                <input
                  type="tel"
                  placeholder="Enter Phone Number"
                  value={phone}
                  style={{ height: 42 }}
                  className="ems-fm-input"
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d{0,15}$/.test(val)) {
                      setPhone(val);
                      setErrors((p) => ({ ...p, phone: "" }));
                    }
                  }}
                />
              </div>
              {errors.phone
                ? <span className="ems-fm-err">{errors.phone}</span>
                : <span style={{ fontSize: 12, color: "var(--ems-faint)", fontWeight: 500 }}>Number used for alerts or account recovery.</span>
              }
            </div>

            <div className="ems-auth-meta" style={{ marginBottom: 24 }}>
              <label className="ems-auth-remember">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => { setAgree(e.target.checked); setErrors((p) => ({ ...p, agree: "" })); }}
                />
                I agree to the{" "}
                <Link to="/terms" style={{ color: "var(--ems-primary)", fontWeight: 650 }}>Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" style={{ color: "var(--ems-primary)", fontWeight: 650 }}>Privacy Policy</Link>.
              </label>
            </div>
            {errors.agree && <span className="ems-fm-err" style={{ display: "block", marginBottom: 12, marginTop: -16 }}>{errors.agree}</span>}

            <div className="ems-auth-actions">
              <button type="submit" className="ems-btn ems-primary" disabled={loading}>
                {loading ? <><SpinSVG /> Creating account…</> : "Continue"}
              </button>
            </div>

            <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "var(--ems-muted)", fontWeight: 500 }}>
              Already have an account?{" "}
              <Link to="/login" style={{ color: "var(--ems-primary)", fontWeight: 650, textDecoration: "none" }}>Login</Link>
            </p>
          </form>

        </div>
      </div>

      {/* ── Right panel — brand hero ── */}
      <div className="ems-auth-right">
        <div className="ems-auth-hero">
          <div className="ems-auth-hero-mark">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <path d="M5 8h16M5 13h10M5 18h16" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="ems-auth-hero-h">{appName}</h2>
          <p className="ems-auth-hero-p">
            {description || "Start your free trial and experience the full power of our platform today."}
          </p>
          <div className="ems-auth-trust">
            <span className="ems-auth-trust-dot" />
            Join thousands of happy users.
          </div>
        </div>
        <p className="ems-auth-copy">&copy; {new Date().getFullYear()} {appName}. All rights reserved.</p>
      </div>

      {/* ── Conflict modal ── */}
      {showConflictModal.show && (
        <div className="ems-org-overlay">
          <div className="ems-org-picker">
            <div className="ems-org-picker-hd">
              <img src={logo} alt="Logo" style={{ height: 42 }} />
              <h3 className="ems-org-picker-h">{showConflictModal.title}</h3>
              <p className="ems-org-picker-sub">{showConflictModal.message}</p>
            </div>
            <div className="ems-org-foot">
              <button
                type="button"
                className="ems-btn ems-ghost"
                onClick={() => setShowConflictModal({ ...showConflictModal, show: false })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ems-btn"
                onClick={() => {
                  setShowConflictModal({ ...showConflictModal, show: false });
                  navigate("/login");
                }}
              >
                Login
              </button>
              <button
                type="button"
                className="ems-btn ems-primary"
                onClick={() => {
                  setShowConflictModal({ ...showConflictModal, show: false });
                  if (showConflictModal.actionType === "email") {
                    navigate("/email-register", { state: { email } });
                  } else if (showConflictModal.actionType === "phone") {
                    navigate("/email-register", { state: { phone } });
                  }
                }}
              >
                Continue Registration
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FreeTrialPage;
